import { Injectable } from '@angular/core';
import { LMToolbarServiceAdapter, LMToolbarServiceBase } from '@toolbar/index';
import { DrillPointsParams, LMContextService } from '@services/index';
import { LMModel, LMModelField } from '@form/model';
import { AppConstants } from '@utils/app.constants';
import { defer } from 'rxjs';

@Injectable()
export class LMFieldOptionsToolbarService extends LMToolbarServiceBase {
  constructor(
    public lmToolbarServiceAdapter: LMToolbarServiceAdapter,
    private lmContextService: LMContextService
  ) {
    super(lmToolbarServiceAdapter);
  }

  public buildContextMenu(
    response: AjaxBeforeOpenResponseFunction,
    options: any,
    field: any,
    specField: LMSpecField,
    showCompact: boolean,
    fieldLabel: string,
    modelField: LMModelField,
    model: LMModel
  ) {
    const contextActions = this.getContextMenuActions(fieldLabel);

    if (options.hasOwnProperty('contextElement') && options.contextElement.length > 0) {
      // build drill around menu
      const menuElement = options.contextElement[0];
      const id = menuElement.getAttribute('id');

      if (id === contextActions.textVariables.actionReference.id) {
        const textVars = field.textVariables;
        let menuResponse = '';
        const subMenuButtons: Array<LMToolbarButton> = this.createToolbarMenuItemsFromTextVariables(textVars);

        for (let index = 0; textVars.length > index; index++) {
          const menuItem: LMToolbarButton = subMenuButtons[index];
          menuResponse +=
            `<li attr.disabled="${menuItem.state.disabled}">
              <a id="TextVariables" data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
            </li>`;
        }
        response(menuResponse);

      } else if (id === contextActions.drillAroundMenu.actionReference.id) {
        if (model.isCreateAction) {
          let menuResponse = '';
          const noActionMenu: LMToolbarButton = {
            actionReference: { id: 'noop', type: 'noop', label: Soho.Locale.translate('NoDrillsAvailable') },
            state: { disabled: false, visible: true }
          };

          menuResponse +=
            `<li attr.disabled="${noActionMenu.state.disabled}">
              <a id="${noActionMenu.actionReference.id}" data-action-reference='${JSON.stringify(noActionMenu.actionReference)}'>${noActionMenu.actionReference.label}</a>
            </li>`;

          response(menuResponse);

        } else {
          defer(async () =>  // defer returns an observable
            await this.buildDrillAroundMenu(field, model)
          )
            .subscribe((drillResponse: LMDrillResponse) => {
              let menuResponse = '';
              if (drillResponse.status === 'COMPLETED'
              && Array.isArray(drillResponse.drillPoints)
              && drillResponse.drillPoints.length > 0) {
              const actionRefs: Array<LMBusActionReference> = this.drillActionsToActionRefArray(drillResponse.drillPoints);
              const subMenuButtons: Array<LMToolbarButton> = this.createToolbarMenuItemsFromActionReferences(null, actionRefs);

                for (let index = 0; subMenuButtons.length > index; index++) {
                  const menuItem: LMToolbarButton = subMenuButtons[ index ];
                  menuResponse +=
                  `<li attr.disabled="${menuItem.state.disabled}">
                    <a id="related" data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
                  </li>`;
                }

              } else {
                const noActionMenu: LMToolbarButton = {
                  actionReference: { id: 'noop', type: 'noop', label: Soho.Locale.translate('NoDrillsAvailable') },
                  state: { disabled: false, visible: true }
                };
                menuResponse +=
                `<li attr.disabled="${noActionMenu.state.disabled}">
                  <a id="${noActionMenu.actionReference.id}" data-action-reference='${JSON.stringify(noActionMenu.actionReference)}'>${noActionMenu.actionReference.label}</a>
                </li>`;
              }
              response(menuResponse);
            });
        }
      }
    } else {
      let initialResponse = '';
      // only show Define if the field is definable, there is a value in the field, and we are not on a smartphone
      if (specField.definable && !modelField.isBlank && !(this.lmContextService.getUIType() === 'SMARTPHONE')) {
        const menuItem: LMToolbarButton = contextActions.define;
        const id: string = menuItem.actionReference.id + 'Event';
        initialResponse +=
          `<li attr.disabled="${menuItem.state.disabled}">
            <a id="${id}" data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
          </li>`;
      }

      if (modelField.displayFieldHistory && !model.isCreateAction) {
        let menuItem: LMToolbarButton = contextActions.showHistory;
        let id: string = menuItem.actionReference.id + 'Event';
        initialResponse +=
          `<li attr.disabled="${menuItem.state.disabled}">
            <a id="${id}" data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
          </li>`;

        menuItem = contextActions.showPendingChanges;
        id = menuItem.actionReference.id + 'Event';
        initialResponse +=
          `<li attr.disabled="${menuItem.state.disabled}">
            <a id="${id}" data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
          </li>`;
      }

      if (specField.drillable) {
        const menuItem: LMToolbarButton = contextActions.drillAroundMenu;
        initialResponse +=
          `<li attr.disabled="${menuItem.state.disabled}" class="submenu">
            <a data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
            <ul id="${menuItem.actionReference.id}" class="popupmenu"></ul>
          </li>`;
      }

      if (field.textVariables) {
        // the ajax callback is assuming the id is on the ul. If you take the id off the ul, then the text variable replacement
        // submenu would no longer work
        const menuItem: LMToolbarButton = contextActions.textVariables;
        initialResponse +=
          `<li attr.disabled="${menuItem.state.disabled}" id="${menuItem.actionReference.id}" class="submenu">
            <a data-action-reference='${JSON.stringify(menuItem.actionReference)}'>${menuItem.actionReference.label}</a>
            <ul id="${menuItem.actionReference.id}" class="popupmenu"></ul>
          </li>`;
      }

      if (specField.translatable && !model.isCreateAction) {
        const menuItem: LMToolbarButton = contextActions.translate;
        const id = menuItem.actionReference.id + 'Event';
        initialResponse +=
            `<li attr.disabled="${menuItem.state.disabled}">
              <a id="${id}"
                  data-action-reference='${JSON.stringify(menuItem.actionReference)}'
              >${menuItem.actionReference.label}</a>
            </li>`;
      }
      response(initialResponse);
    }
  };

  private createToolbarMenuItemsFromTextVariables(textVariables: Array<LMTextVariables>): Array<LMToolbarButton> {
    return <LMToolbarButton[]>textVariables.map(
      textVariable => {
        const actionRef: LMBusActionReference = {
          id:    textVariable.id,
          label: textVariable.label,
          type:  'standard',
        };
        const toolbarButton: LMToolbarButton = {
          actionReference: actionRef,
          state:           { disabled: null, visible: true }
        };
        return toolbarButton;
      });
  }

  private async buildDrillAroundMenu(field: any, model: LMModel) {
    const drillPoints: DrillPointsParams = { model, fieldId: field.bindId };
    return await this.lmToolbarServiceAdapter.lmDrillService.getFieldDrillPoints(drillPoints).toPromise();
  }

  private getContextMenuActions(fieldLabel: string): { [ key: string ]: LMToolbarButton } {
    return {
      drillAroundMenu: {
        actionReference: {
          id: AppConstants.STANDARD_TOOLBAR_RELATEDMENU_BUTTON,
          type: 'standard',
          label: AppConstants.DRILL_AROUND_LABEL
        }, state: { disabled: false, visible: true }
      },
      showHistory:        {
        actionReference: {
          id: AppConstants.FORM_CONTEXTMENUACTION_SHOWFIELDHISTORY,
          type: 'standard',
          label: Soho.Locale.translate('ShowFieldHistory')
        }, state: { disabled: false, visible: true }
      },
      showPendingChanges: {
        actionReference: {
          id: AppConstants.FORM_CONTEXTMENUACTION_SHOWPENDINGCHANGES,
          type: 'standard',
          label: Soho.Locale.translate('ShowPendingChanges')
        }, state: { disabled: false, visible: true }
      },
      hasCompactFormat: {
        actionReference: {
          id: AppConstants.FORM_CONTEXTMENUACTION_SHOWCOMPACTFORM,
          type: 'standard',
          label: fieldLabel
        }, state: { disabled: false, visible: true }
      },
      textVariables: {
        actionReference: {
          id: AppConstants.FORM_CONTEXTMENUACTION_TEXTVARIABLES,
          type: 'standard',
          label: Soho.Locale.translate('TextVariableReplacement')
        }, state: { disabled: false, visible: true }
      },
      define:      {
        actionReference: {
          id: AppConstants.FORM_CONTEXTMENUACTION_DEFINE,
          type: 'standard',
          label: Soho.Locale.translate('Define')
        }, state: { disabled: false, visible: true }
      },
      translate: {
        actionReference: {
          id: AppConstants.FORM_CONTEXTMENUACTION_TRANSLATE,
          type: 'standard',
          label: Soho.Locale.translate('Translate')
        }, state: { disabled: false, visible: true }
      },
    };
  }
}
